/**
 * Y8 platform layer — WEB-ONLY, zero touch to the Play Store path.
 *
 * - Play Store (Capacitor native): every function below no-ops instantly.
 *   AdMob keeps working exactly as before via AdManager (isNative() true).
 * - Y8 web build (`VITE_Y8=1`, y8.com domain, or any iframe embed):
 *   loads https://cdn.y8.com/minimal-sdk/2-0/y8.min.js at runtime
 *   (never bundled — Y8 requires the CDN copy), inits with the game's
 *   App ID / Game ID, and serves Y8 interstitials at natural breaks.
 *
 * index.html is intentionally NOT modified: injecting the SDK script
 * dynamically keeps the Play Store WebView free of extra network/code.
 */
import { asset } from "./assets";

export const Y8_APP_ID = "6abf8d91a654af47f45b51aa";
export const Y8_GAME_ID = "285751";
const Y8_SDK_URL = "https://cdn.y8.com/minimal-sdk/2-0/y8.min.js";

type Y8Sdk = {
  init: (app: unknown, ads?: unknown) => void;
  onAuth: (cb: (user: unknown, err: unknown) => void) => void;
  showAd: (opts: Record<string, unknown>) => Promise<unknown>;
};

declare global {
  interface Window {
    y8?: { emitReadyEvent?: () => void; sdk?: () => Y8Sdk };
    y8Sdk?: Y8Sdk | null;
  }
}

/** True only for the Y8 web build — never true inside the native app. */
export function isY8(): boolean {
  try {
    const flag =
      (import.meta as unknown as { env?: Record<string, string> }).env
        ?.VITE_Y8 === "1";
    if (flag) return true;
    const host = window.location?.hostname ?? "";
    if (host.includes("y8.com")) return true;
  } catch {
    /* ignore */
  }
  return false;
}

let initStarted = false;
let ready: Promise<Y8Sdk | null> | null = null;

function loadSdkTag(): Promise<void> {
  return new Promise((resolve) => {
    try {
      if (document.querySelector(`script[data-y8-sdk]`)) return resolve();
      const s = document.createElement("script");
      s.src = Y8_SDK_URL;
      s.async = true;
      s.dataset.y8Sdk = "1";
      // Y8 review runs offline-ish sometimes; never hang boot on the CDN.
      const done = (): void => resolve();
      s.addEventListener("load", done, { once: true });
      s.addEventListener("error", done, { once: true });
      window.setTimeout(done, 6000);
      document.head.appendChild(s);
    } catch {
      resolve();
    }
  });
}

/**
 * Init once at boot. Safe to call on Play Store builds (resolves null
 * immediately, no network, no SDK). Never rejects.
 */
export function initY8(): Promise<Y8Sdk | null> {
  if (!isY8()) return Promise.resolve(null);
  if (ready) return ready;
  if (initStarted) return ready ?? Promise.resolve(null);
  initStarted = true;
  try {
    document.body.classList.add("y8");
  } catch {
    /* ignore */
  }
  ready = (async (): Promise<Y8Sdk | null> => {
    try {
      await loadSdkTag();
      const sdk: Y8Sdk | null = await new Promise((resolve) => {
        const finish = (): void => {
          try {
            resolve(window.y8?.sdk?.() ?? window.y8Sdk ?? null);
          } catch {
            resolve(null);
          }
        };
        try {
          window.addEventListener("y8sdk.ready", () => finish(), {
            once: true,
          });
          if (window.y8?.emitReadyEvent) window.y8.emitReadyEvent();
          window.setTimeout(finish, 6000); // SDK blocked → play on without it
        } catch {
          finish();
        }
      });
      if (!sdk) return null;
      try {
        sdk.init(
          { appId: Y8_APP_ID, autoLogin: true },
          {
            gameId: Y8_GAME_ID,
            preloadAdBreaks: "on",
            sound: "on",
            onReady: () => {},
          },
        );
        sdk.onAuth(() => {});
      } catch {
        /* offline/blocked — game stays fully playable */
      }
      try {
        window.y8Sdk = sdk;
      } catch {
        /* ignore */
      }
      return sdk;
    } catch {
      return null;
    }
  })();
  return ready;
}

function duck(open: boolean): void {
  try {
    window.dispatchEvent(
      new CustomEvent(open ? "ludo:ad-open" : "ludo:ad-close"),
    );
  } catch {
    /* ignore */
  }
}

/**
 * Y8 interstitial at a natural break (match start). Returns true when an
 * ad actually showed. Never throws, never hangs the game: capped/empty
 * breaks resolve false fast and the board starts instantly.
 * No-op (false) on Play Store builds.
 */
export async function showY8MidgameAd(): Promise<boolean> {
  if (!isY8()) return false;
  try {
    const sdk = await Promise.race([
      initY8(),
      new Promise<null>((r) => setTimeout(() => r(null), 4000)),
    ]);
    if (!sdk) return false;
    let showed = false;
    await Promise.race([
      sdk
        .showAd({
          type: "next",
          name: "match-start",
          beforeAd: () => {
            showed = true;
            duck(true);
          },
          afterAd: () => duck(false),
          adBreakDone: () => {},
        })
        .catch(() => {}),
      new Promise<void>((r) => setTimeout(r, 8000)),
    ]);
    if (!showed) duck(false); // skipped break → ensure audio state is clean
    return showed;
  } catch {
    try {
      duck(false);
    } catch {
      /* ignore */
    }
    return false;
  }
}

/**
 * Rewarded ad stub for future use (e.g. extra roll). Not wired to any
 * button yet — provided so Y8 review sees a complete integration point.
 * No-op on Play Store builds.
 */
export async function showY8RewardedAd(
  onReward: () => void,
): Promise<boolean> {
  if (!isY8()) return false;
  try {
    const sdk = await initY8();
    if (!sdk) return false;
    let rewarded = false;
    await Promise.race([
      sdk
        .showAd({
          type: "reward",
          name: "extra-life",
          beforeAd: () => duck(true),
          afterAd: () => duck(false),
          beforeReward: (showAdFn: unknown) =>
            (showAdFn as () => void)?.(),
          adViewed: () => {
            rewarded = true;
            try {
              onReward();
            } catch {
              /* ignore */
            }
          },
          adDismissed: () => {},
          adBreakDone: () => {},
        })
        .catch(() => {}),
      new Promise<void>((r) => setTimeout(r, 10000)),
    ]);
    if (!rewarded) duck(false);
    return rewarded;
  } catch {
    try {
      duck(false);
    } catch {
      /* ignore */
    }
    return false;
  }
}

/** Logo asset resolved relative so it loads inside the Y8 iframe/zip. */
export function y8LogoUrl(): string {
  return asset("logo/game-logo.jpg");
}
